import { useMutation } from '@tanstack/react-query';
import {
    EmailAuthProvider,
    User,
    reauthenticateWithCredential,
    signInWithEmailAndPassword,
    updatePassword,
} from 'firebase/auth';
import { storeCredentials } from '../../../apiClient';
import { useAuth } from '../AuthProvider';
import {
    ChangePasswordError,
    NO_FIREBASE_SESSION,
    NO_PASSWORD_PROVIDER,
} from '../change-password';
import { auth } from '../firebase';

export interface ChangePasswordInput {
    currentPassword: string;
    newPassword: string;
}

/**
 * Let the signed-in org member change their own password.
 *
 * Runs entirely against the Firebase client SDK — the same place `useSignIn`
 * and `useSendPasswordResetEmail` already live — because the credential being
 * changed belongs to Firebase, not to our database. There is no organization
 * scoping to do: a user can only ever reach their OWN `auth.currentUser`, and
 * Firebase requires the current password before it will accept a new one.
 *
 * ## When the SDK has no session
 *
 * `AuthProvider` decides you are signed in by decoding the `idToken` in
 * localStorage; the Firebase SDK keeps its own session in IndexedDB. They are
 * separate stores, so the app can look signed in while `auth.currentUser` is
 * null, and `updatePassword` needs a real `User`.
 *
 * That case is handled silently rather than by asking anyone to sign out and
 * back in. Everything needed to rebuild the session is already here: the email
 * from the token the app is holding, and the current password being typed into
 * the form. `signInWithEmailAndPassword` establishes the session AND proves the
 * current password — which is exactly what the re-authentication below does
 * anyway, so the fallback costs the user nothing and shows them nothing.
 */
export const useChangePassword = (onSuccess: () => void) => {
    // Fallback identity for the no-SDK-session case above. This is the `email`
    // claim of the already-verified token, not user input.
    const { currentUser } = useAuth();
    const claimEmail =
        typeof currentUser?.email === 'string' ? currentUser.email : null;

    return useMutation({
        mutationKey: ['changePassword'],
        mutationFn: async ({ currentPassword, newPassword }: ChangePasswordInput) => {
            // The SDK restores the persisted session asynchronously, so a
            // bare `auth.currentUser` read right after a page load can be null
            // for a user who is very much signed in.
            await auth.authStateReady();

            const signedIn = auth.currentUser;
            const email = signedIn?.email ?? claimEmail;
            // Neither an SDK session nor an email claim. Nothing to identify
            // the account by, and nothing the user can type to fix it.
            if (!email) {
                throw new ChangePasswordError(NO_FIREBASE_SESSION);
            }

            let user: User;
            if (signedIn?.email) {
                const hasPasswordProvider = signedIn.providerData.some(
                    (provider) => provider.providerId === EmailAuthProvider.PROVIDER_ID,
                );
                // An empty `providerData` is not proof of anything (it happens
                // for some custom-token sessions), so only reject when we
                // positively know the account signs in some other way.
                if (signedIn.providerData.length > 0 && !hasPasswordProvider) {
                    throw new ChangePasswordError(NO_PASSWORD_PROVIDER);
                }
                await reauthenticateWithCredential(
                    signedIn,
                    EmailAuthProvider.credential(email, currentPassword),
                );
                user = signedIn;
            } else {
                // No SDK session — sign in to build one. Same credential check
                // as the branch above, so a wrong current password still fails
                // here, with the same error code.
                const credentials = await signInWithEmailAndPassword(
                    auth,
                    email,
                    currentPassword,
                );
                user = credentials.user;
            }

            await updatePassword(user, newPassword);

            // Sign in again, with the NEW password, and store what that mints.
            //
            // This is not belt-and-braces — without it the operator gets
            // force-logged-out roughly a minute after a SUCCESSFUL password
            // change, and no token refresh can prevent it:
            //
            // 1. The change bumps `tokensValidAfterTime` on the Firebase user
            //    record to the moment it happened.
            // 2. Every dashboard request runs through the API's
            //    `assertSessionNotRevoked` (`libs/auth/token-revocation.ts`,
            //    wired into `AdminAuthService.verifyToken`), which rejects a
            //    token whose `auth_time` predates that instant.
            // 3. `auth_time` records when the user last AUTHENTICATED, and a
            //    refresh deliberately does not move it — that is the whole
            //    point of the claim, and `token-revocation.ts` says so where
            //    it explains why it compares `auth_time` and not `iat`. So
            //    `getIdToken(true)` returns a token that is brand new and
            //    still fails the check.
            // 4. Both timestamps are truncated to whole seconds, so whether
            //    the re-auth above and the update below land in the same
            //    second decides the outcome. That made the failure
            //    intermittent, and the API's 60 s revocation cache delayed it
            //    past the point where anyone would connect it to this form.
            //
            // A fresh `signInWithPassword` is the only thing that moves
            // `auth_time` forward, and it also hands back the refresh token
            // that replaces the one the change just revoked in localStorage.
            //
            // Swallowed on failure on purpose: the password change already
            // committed server-side, and surfacing an error here would send
            // the user back to a form whose "current password" is now the old
            // one. Worst case they are asked to sign in again — with the new
            // password, which is exactly what they just set.
            try {
                await reauthenticateWithCredential(
                    user,
                    EmailAuthProvider.credential(email, newPassword),
                );
                storeCredentials(await user.getIdToken(), user.refreshToken);
            } catch {
                // Keep the stale credentials; apiClient signs the user out if
                // they turn out to be dead.
            }
        },
        onSuccess,
    });
};
